import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import { Dialog } from 'radix-ui'
import { CornerDownLeft, Search, Clock, Star, User, Banknote, LifeBuoy, Loader2, Zap } from 'lucide-react'
import { api } from '@/api/client'
import { useRecents } from '@/lib/personal'
import { usePreferences } from '@/lib/preferences'
import { useVisibleNav } from './Sidebar'
import { useAuth } from '@/hooks/useAuth'
import { openAssistant } from '@/api/assistant'

/** Quick actions from anywhere (E6 "quick-create"): each opens the form it names, role-checked. */
interface PaletteAction { id: string; label: string; keywords: string; run: () => void }

/**
 * ⌘K / Ctrl+K command palette (spec 21 §3.3.5, E-UX): pages the user may open (role-filtered,
 * synonyms), favourites and recently viewed pages, and RECORDS from `GET /search` — people,
 * payroll runs, helpdesk tickets — scoped by the server to what this user may see. Remote results
 * are debounced (250 ms) and never block the local groups.
 */
interface SearchHit { type: 'employee' | 'payroll_run' | 'ticket'; id: string; title: string; subtitle: string; path: string }
const TYPE_ICON = { employee: User, payroll_run: Banknote, ticket: LifeBuoy } as const
const TYPE_LABEL = { employee: 'People found', payroll_run: 'Payroll runs found', ticket: 'Tickets found' } as const

const groupCls = '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground'
const itemCls = 'group flex h-10 cursor-pointer items-center gap-3 rounded-md px-2.5 text-sm text-foreground data-[selected=true]:bg-surface-hover'

export function CommandPalette({ open, onOpenChange, onShortcuts }: { open: boolean; onOpenChange: (o: boolean) => void; onShortcuts?: () => void }) {
  const navigate = useNavigate()
  const sections = useVisibleNav()
  const recents = useRecents()
  const { prefs, resolvedTheme, update } = usePreferences()
  const { hasRole } = useAuth()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 250)
    return () => window.clearTimeout(t)
  }, [query])
  useEffect(() => { if (!open) { setQuery(''); setDebounced('') } }, [open])

  const remote = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: async () => ((await api.get('/api/v1/search', { params: { q: debounced } })).data as { items: SearchHit[] }).items,
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
    retry: false,
    meta: { inlineError: true },
  })

  const go = (to: string) => {
    onOpenChange(false)
    navigate(to)
  }

  const reachable = new Set(sections.flatMap((x) => x.items.map((i) => i.to)))
  const hr = hasRole(['HR_ADMIN', 'HR_EXECUTIVE'])
  const actions: PaletteAction[] = [
    ...(reachable.has('/ess/leaves') ? [{ id: 'leave', label: 'Apply for leave', keywords: 'new leave request time off vacation', run: () => go('/ess/leaves?apply=1') }] : []),
    ...(reachable.has('/helpdesk') ? [{ id: 'ticket', label: 'Raise a helpdesk ticket', keywords: 'new ticket support issue complaint', run: () => go('/helpdesk?new=1') }] : []),
    ...(hr && reachable.has('/employees') ? [{ id: 'employee', label: 'Add an employee', keywords: 'new hire create employee joiner', run: () => go('/employees?new=1') }] : []),
    ...(hr && reachable.has('/holidays') ? [{ id: 'holiday', label: 'Add a holiday', keywords: 'new holiday calendar', run: () => go('/holidays?new=1') }] : []),
    { id: 'assistant', label: query.trim().length > 2 ? `Ask the assistant: "${query.trim()}"` : 'Ask the assistant', keywords: 'help assistant ask question how law pf esi gratuity doubt', run: () => { const q = query.trim(); onOpenChange(false); openAssistant(q.length > 2 ? q : undefined) } },
    { id: 'password', label: 'Change my password', keywords: 'security password reset', run: () => go('/ess/profile?tab=security') },
    { id: '2fa', label: 'Set up two-step verification', keywords: '2fa mfa otp authenticator security', run: () => go('/ess/profile?tab=security') },
    { id: 'theme', label: resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode', keywords: 'theme dark light appearance night', run: () => { onOpenChange(false); void update({ theme: resolvedTheme === 'dark' ? 'light' : 'dark' }).catch(() => undefined) } },
    ...(onShortcuts ? [{ id: 'keys', label: 'Show keyboard shortcuts', keywords: 'keys hotkeys help ?', run: () => { onOpenChange(false); onShortcuts() } }] : []),
  ]
  const hits = remote.data ?? []
  const hitGroups = (Object.keys(TYPE_LABEL) as SearchHit['type'][]).map((t) => ({ type: t, rows: hits.filter((h) => h.type === t) })).filter((g) => g.rows.length)
  const favourites = prefs.favourites ?? []

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-overlay/30 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[14vh] z-[70] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-overlay border border-border bg-card shadow-e4 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98]"
        >
          <Dialog.Title className="sr-only">Search pages, people and records</Dialog.Title>
          <Command loop className="flex flex-col">
            <div className="flex items-center gap-2.5 border-b border-border px-4">
              {remote.isFetching ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : <Search className="size-4 text-muted-foreground" />}
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder="Search pages, people, payroll runs, tickets…"
                className="h-12 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-content-placeholder"
              />
              <kbd className="rounded border border-border bg-background px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">Esc</kbd>
            </div>
            <Command.List className="max-h-[min(60vh,420px)] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-10 text-center text-sm text-muted-foreground">
                {debounced.length >= 2 && remote.isFetching ? 'Searching…' : query.trim().length === 1 ? 'Keep typing to search people and records.' : 'Nothing matches that. Try a name, an employee code or a page.'}
              </Command.Empty>

              {hitGroups.map((g) => (
                <Command.Group key={g.type} heading={TYPE_LABEL[g.type]} className={groupCls}>
                  {g.rows.map((h) => {
                    const Icon = TYPE_ICON[h.type]
                    return (
                      <Command.Item key={`${h.type}:${h.id}`} value={`${h.type}:${h.id} ${h.title} ${h.subtitle} ${query}`} onSelect={() => go(h.path)} className={itemCls}>
                        <span className="grid size-7 place-items-center rounded-md border border-border bg-card text-muted-foreground"><Icon className="size-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{h.title}</span>
                          {h.subtitle && <span className="block truncate text-xs text-muted-foreground">{h.subtitle}</span>}
                        </span>
                        <CornerDownLeft className="size-3.5 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" />
                      </Command.Item>
                    )
                  })}
                </Command.Group>
              ))}

              <Command.Group heading="Actions" className={groupCls}>
                {actions.map((a) => (
                  <Command.Item key={`act:${a.id}`} value={`action ${a.label}`} keywords={a.keywords.split(' ')} onSelect={a.run} className={itemCls}>
                    <span className="grid size-7 place-items-center rounded-md border border-border bg-card text-link"><Zap className="size-4" /></span>
                    <span className="flex-1 truncate">{a.label}</span>
                    <CornerDownLeft className="size-3.5 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" />
                  </Command.Item>
                ))}
              </Command.Group>

              {favourites.length > 0 && (
                <Command.Group heading="Favourites" className={groupCls}>
                  {favourites.map((f) => (
                    <Command.Item key={`fav:${f.path}`} value={`fav ${f.title} ${f.path}`} onSelect={() => go(f.path)} className={itemCls}>
                      <span className="grid size-7 place-items-center rounded-md border border-border bg-card text-warning"><Star className="size-4 fill-current" /></span>
                      <span className="flex-1 truncate">{f.title}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {recents.length > 0 && query.trim() === '' && (
                <Command.Group heading="Recently viewed" className={groupCls}>
                  {recents.map((r) => (
                    <Command.Item key={`recent:${r.path}`} value={`recent ${r.title} ${r.path}`} onSelect={() => go(r.path)} className={itemCls}>
                      <span className="grid size-7 place-items-center rounded-md border border-border bg-card text-muted-foreground"><Clock className="size-4" /></span>
                      <span className="flex-1 truncate">{r.title}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {sections.map((s) => (
                <Command.Group key={s.title} heading={s.title} className={groupCls}>
                  {s.items.map((it) => (
                    <Command.Item
                      key={it.to}
                      value={`${it.label} ${s.title}`}
                      keywords={it.keywords ? it.keywords.split(' ') : undefined}
                      onSelect={() => go(it.to)}
                      className={itemCls}
                    >
                      <span className="grid size-7 place-items-center rounded-md border border-border bg-card text-muted-foreground group-data-[selected=true]:text-foreground">
                        <it.icon className="size-4" />
                      </span>
                      <span className="flex-1 truncate">{it.label}</span>
                      <CornerDownLeft className="size-3.5 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" />
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
